import { useRef, useState } from "react";
import { Link } from "react-router";
import { RouteFactory } from "@app/routePaths";
import Ad from "@components/Advertisement/Ad";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import { GADataProps } from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import { useBreadCrumbState } from "@components/Header/helpers";
import {
  DAILY_NEWSLETTERS,
  NEW_NEWSLETTERS,
  PREMIUM_NEWSLETTERS,
  WEEKLY_NEWSLETTERS,
} from "@components/Newsletter/constants";
import NewsletterSubscriptionPanel from "@components/Newsletter/NewsletterSubscriptionPanel";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import useHandleSelectedNewsletter from "@hooks/useHandleSelectedNewsletters";
import { useInjectClientData } from "@hooks/useInjectClientData";
import NewsletterAnchorLinks from "@routes/Newsletter/SignUp/components/NewsletterAnchorLinks";
import NewsletterSections from "@routes/Newsletter/SignUp/components/NewsletterSections";
import SectionSubhHeading from "@routes/Section/components/SectionSubhHeading";
import { DefaultNavigationData } from "@typings/Page";
import { sectionNavigationItems } from "@util/constant";
import { cn, scrollToNewsletterSection } from "@util/helpers";

const NewsletterSignupPageContent = ({
  seoFooterData,
  gaProps,
}: DefaultNavigationData & { gaProps: GADataProps }): React.ReactElement => {
  useInjectClientData({ gaProps });

  const [email, setEmail] = useState("");
  const newNewsletterRef = useRef<HTMLDivElement>(null);
  const premiumNewsletterRef = useRef<HTMLDivElement>(null);
  const dailyNewsletterRef = useRef<HTMLDivElement>(null);
  const weeklyNewsletterRef = useRef<HTMLDivElement>(null);

  const [selectedNewsletters, handleSelectedNewsletter] =
    useHandleSelectedNewsletter();
  const { breadcrumbsRef } = useBreadCrumbState();

  return (
    <div className="w-full" data-testid="newsletter-sign-up-page-component">
      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header />

      <PulseEntry />

      <Container>
        <h1
          className={cn(
            "pb-0 pt-6 font-playfair text-6xl font-semibold text-gray-850",
            "md:text-8xl"
          )}
        >
          NEWSLETTERS
        </h1>

        <SectionSubhHeading
          to={`${RouteFactory.newsletterSignup}?ref=listing-menubar`}
          className="inline-block pb-3 font-poppins text-xs font-light text-gray-850 underline"
          reloadDocument
        >
          Select Your Newsletters
        </SectionSubhHeading>

        <div ref={breadcrumbsRef}>
          <Breadcrumb
            className="m-auto"
            parentCategory={RouteFactory.newsletters.replace("/", "")}
            categoryDataMapping={sectionNavigationItems}
            extraBreadcrumbItems={
              <div className="relative box-border flex shrink-0 items-center border-r border-gray-175 pr-3 font-poppins text-sm tracking-tightest text-gray-850 hover:opacity-80">
                <Link
                  to={`${RouteFactory.newsletters}?ref=listing-menubar`}
                  reloadDocument
                >
                  Top stories
                </Link>
              </div>
            }
          />
        </div>
      </Container>

      <div
        className="newsletter-signup-page container !mt-6 px-3"
        data-testid="newsletter-signup-page"
      >
        <div className="grid grid-cols-12 gap-3 md:gap-0">
          <div className="col-span-12 xl:col-span-3">
            <p className="font-public-sans text-lg font-normal text-gray-850">
              Select the newsletters that suit your needs and get them sent
              straight to your inbox.
            </p>

            <NewsletterAnchorLinks
              scrollToNewsletterSection={(newsletter) => {
                scrollToNewsletterSection(newsletter, weeklyNewsletterRef);
              }}
            />
          </div>

          <div className="col-span-12 xl:col-span-9">
            <NewsletterSections
              title="NEW"
              newsletters={NEW_NEWSLETTERS}
              selectedNewsletters={selectedNewsletters}
              handleSelectedNewsletter={handleSelectedNewsletter}
              sectionRef={newNewsletterRef}
            />

            <NewsletterSections
              title="Premium NEWSLETTERS"
              newsletters={PREMIUM_NEWSLETTERS}
              selectedNewsletters={selectedNewsletters}
              handleSelectedNewsletter={handleSelectedNewsletter}
              sectionRef={premiumNewsletterRef}
            />

            <NewsletterSections
              title="Daily NEWSLETTERS"
              newsletters={DAILY_NEWSLETTERS}
              selectedNewsletters={selectedNewsletters}
              handleSelectedNewsletter={handleSelectedNewsletter}
              sectionRef={dailyNewsletterRef}
              rootClassname={"xl:pt-0 xl:border-none pt-3 border-t"}
            />

            <NewsletterSections
              title="Weekly NEWSLETTERS"
              newsletters={WEEKLY_NEWSLETTERS}
              selectedNewsletters={selectedNewsletters}
              handleSelectedNewsletter={handleSelectedNewsletter}
              sectionRef={weeklyNewsletterRef}
            />
          </div>
        </div>
      </div>

      {selectedNewsletters.length > 0 ? (
        <NewsletterSubscriptionPanel
          selectedNewsletters={selectedNewsletters}
          handleSelectedNewsletter={handleSelectedNewsletter}
          email={email}
          setEmail={setEmail}
        />
      ) : null}

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default NewsletterSignupPageContent;
